/**
 * Transcript Panel
 *
 * Self-contained transcript display for use in the unified session viewer.
 * Reuses useTranscriptSocket hook and shares settings with standalone transcript viewer.
 * Touch-friendly controls with min 44px tap targets.
 */

import {
	createFollowTracker,
	cssFontFamily,
	DEFAULT_TRANSCRIPT_VIEWER_SETTINGS,
	effectiveTranscriptSettings,
	FOLLOW_LAYOUT_SETTLE_MS,
	type FollowTracker,
	isFollowReleaseKey,
	legibleViewerColors,
	normalizeFontFamily,
	type SessionFileInfo,
	type ThemedTranscriptSettings,
	withColorsFollowTheme,
} from '@polygraf/shared'
import {
	ArrowDownToLine,
	ChevronsDown,
	Loader2,
	Lock,
	LogIn,
	RefreshCw,
	Settings,
	Wifi,
	WifiOff,
} from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router-dom'
import { useSegmentColorGuard } from '../../hooks/use-segment-color-guard'
import { useTranscriptAnnouncer } from '../../hooks/use-transcript-announcer'
import { useTranscriptSocket } from '../../hooks/use-transcript-socket'
import { useAppTheme } from '../../lib/app-theme'
import { useSession } from '../../lib/auth-client'
import { type TranscriptPresetKey, TranscriptSettingsPanel } from './transcript-settings-panel'

// Same range as the transcript viewer route (12–96 px); low-vision users hit
// the old 48 px ceiling on /join and asked for parity (MU review, 26. 8. 2026).
const MIN_FONT_SIZE = 12
const MAX_FONT_SIZE = 96

// The live panel reads one step larger than the standalone viewer; every
// other default (line spacing 1.2 included) is the shared one.
const DEFAULT_SETTINGS: ThemedTranscriptSettings = {
	...DEFAULT_TRANSCRIPT_VIEWER_SETTINGS,
	fontSize: 18,
	colorsFollowTheme: true,
}

// Presets on the live-session panel — the same set it offered before the
// shared panel existed.
const LIVE_PRESETS: readonly TranscriptPresetKey[] = [
	'light',
	'dark',
	'highContrast',
	'sepia',
	'default',
]

const SETTINGS_KEY = 'transcriptViewerSettings'

function loadSettings(): ThemedTranscriptSettings {
	try {
		const saved = localStorage.getItem(SETTINGS_KEY)
		if (saved) {
			// autoScroll is intentionally not restored — see saveSettings. Payloads
			// written by older builds can still carry it, so override it here too.
			const stored = JSON.parse(saved)
			const loaded = { ...DEFAULT_SETTINGS, ...stored, autoScroll: true }
			// A stored illegible text/background pair survives every code fix
			// (MP-182, one Windows browser for three months) — validate it here.
			// An unreadable pair is replaced by the default one, which follows the theme.
			return withColorsFollowTheme(
				legibleViewerColors(
					{ ...loaded, fontFamily: normalizeFontFamily(loaded.fontFamily) },
					DEFAULT_SETTINGS,
				),
				stored,
			)
		}
	} catch {
		// Ignore errors
	}
	return DEFAULT_SETTINGS
}

// Appearance settings persist; auto-scroll deliberately does not, so the
// viewer always opens following the live edge (MU 17. 9. 2026). Shares the
// storage key with the standalone transcript viewer, which does the same.
function saveSettings(settings: ThemedTranscriptSettings): void {
	try {
		const { autoScroll: _autoScroll, ...persisted } = settings
		localStorage.setItem(SETTINGS_KEY, JSON.stringify(persisted))
	} catch {
		// Ignore errors
	}
}

interface TranscriptPanelProps {
	sessionId: string
	className?: string
	hideRefresh?: boolean
	/** Immersive mode: the panel's own header is hidden with the page toolbar. */
	immersive?: boolean
	onConnectionChange?: (connected: boolean) => void
	onFileUploaded?: (file: SessionFileInfo) => void
	onFileRemoved?: () => void
}

export function TranscriptPanel({
	sessionId,
	className = '',
	hideRefresh = false,
	immersive = false,
	onConnectionChange,
	onFileUploaded,
	onFileRemoved,
}: TranscriptPanelProps) {
	const { t } = useTranslation()
	// `stored` is what persists; `settings` is what paints. They differ only in
	// the colours of a reader who never picked their own: those follow the theme.
	const [stored, setSettings] = useState<ThemedTranscriptSettings>(loadSettings)
	const theme = useAppTheme()
	const settings = useMemo(() => effectiveTranscriptSettings(stored, theme), [stored, theme])
	const guardSegmentColors = useSegmentColorGuard(settings.backgroundColor, settings.textColor)
	const [showSettings, setShowSettings] = useState(false)
	const contentRef = useRef<HTMLDivElement>(null)
	// Shared by the scroll listener and the follow loop, which rebases it
	// after moving `scrollTop` itself.
	const followTrackerRef = useRef<FollowTracker | null>(null)

	const {
		document,
		isConnected,
		isConnecting,
		hasWriter,
		activeParagraphId,
		isWriterTyping,
		error,
		accessDenied,
		reconnect,
	} = useTranscriptSocket({ documentId: sessionId, onFileUploaded, onFileRemoved })

	// MP-215: in-band subscribe denial. The embedding pages gate access before
	// mounting this panel, so this state is a defensive backstop (e.g. access
	// revoked mid-session) — without it the panel would sit on "Waiting" forever.
	const location = useLocation()
	const { data: authSession } = useSession()
	const isAuthenticated = !!authSession?.user
	const loginHref = `/auth?returnTo=${encodeURIComponent(location.pathname + location.search)}`

	// F-017: SRs spammed the whole region on each keystroke when aria-live wrapped
	// the document tree. Hook routes only the appended delta to an off-screen
	// announcer at word boundaries — see use-transcript-announcer.ts for rationale.
	const announcerRef = useTranscriptAnnouncer(document, isWriterTyping)

	useEffect(() => {
		onConnectionChange?.(isConnected && hasWriter)
	}, [isConnected, hasWriter, onConnectionChange])

	useEffect(() => {
		saveSettings(stored)
	}, [stored])

	// Release live follow the moment the reader scrolls upward, whatever the
	// input (wheel, touch, scrollbar drag, keys, screen-reader navigation).
	// The standalone viewer has had wheel/touch release since Apr 11; with the
	// footer checkbox gone (MU 4. 9. 2026) this panel needs the same, or the
	// auto-scroll loop below drags a reader back to the end while they read.
	// That loop only ever increases scrollTop, and it rebases the tracker after
	// each of its writes, so it can neither release nor re-arm follow itself.
	useEffect(() => {
		const el = contentRef.current
		if (!el) return
		// Same shared decision the standalone viewer uses. This panel carried
		// its own copy of the logic and inherited three of its defects: no
		// guard against the browser lowering `scrollTop` after a layout change,
		// no latch on release (so a nudge under the tolerance switched follow
		// off for good), and a second pair of the boundary constants free to
		// drift from the exported ones.
		const track = createFollowTracker(el)
		followTrackerRef.current = track
		let settleUntil = 0
		const handleScroll = () => {
			const action = track({ settling: performance.now() < settleUntil })
			if (action === 'release') {
				setSettings((prev) => (prev.autoScroll ? { ...prev, autoScroll: false } : prev))
			} else if (action === 'rearm') {
				// Returning to the end re-arms live follow (MU 17. 9. 2026).
				setSettings((prev) => (prev.autoScroll ? prev : { ...prev, autoScroll: true }))
			}
		}
		// Resizing the panel makes the browser re-clamp `scrollTop`, which must
		// not read as the reader scrolling up.
		const observer = new ResizeObserver(() => {
			settleUntil = performance.now() + FOLLOW_LAYOUT_SETTLE_MS
		})
		observer.observe(el)
		el.addEventListener('scroll', handleScroll, { passive: true })
		return () => {
			observer.disconnect()
			el.removeEventListener('scroll', handleScroll)
			followTrackerRef.current = null
		}
	}, [])

	// Continuously smooth-scroll to bottom while auto-scroll is enabled
	useEffect(() => {
		if (!settings.autoScroll) return

		let rafId: number
		// The scrollTop this loop last wrote. If it is lower now, someone else
		// scrolled up since (a key, the scrollbar, a screen reader): yield that
		// frame instead of writing over it, and let the scroll tracker decide.
		let lastWritten: number | null = null

		const tick = () => {
			const el = contentRef.current
			if (el) {
				const yielded = lastWritten !== null && el.scrollTop < lastWritten - 1
				const gap = el.scrollHeight - el.scrollTop - el.clientHeight
				if (!yielded && gap > 1) {
					el.scrollTop += Math.max(1, gap * 0.15)
					// The loop's own movement is never the reader returning to
					// the end: its scroll event lands a frame later, after a
					// release or a switch-off, and would re-arm follow.
					followTrackerRef.current?.rebase()
				}
				lastWritten = yielded ? null : el.scrollTop
			}
			rafId = requestAnimationFrame(tick)
		}

		// Release follow on the key press itself, before the browser starts its
		// smooth scroll — see isFollowReleaseKey in @polygraf/shared.
		const handleKeyDown = (e: KeyboardEvent) => {
			const target = e.target as HTMLElement | null
			const inFormField = !!target?.closest?.('input, select, textarea, [contenteditable="true"]')
			if (isFollowReleaseKey({ key: e.key, shiftKey: e.shiftKey, inFormField })) {
				// The key may scroll nothing (focus on a header button, already at
				// the top), so no scroll sample will set the latch: set it here, or
				// scrolling back down cannot re-arm follow (MP-247).
				followTrackerRef.current?.leave()
				setSettings((prev) => (prev.autoScroll ? { ...prev, autoScroll: false } : prev))
			}
		}
		window.addEventListener('keydown', handleKeyDown)

		rafId = requestAnimationFrame(tick)
		return () => {
			cancelAnimationFrame(rafId)
			window.removeEventListener('keydown', handleKeyDown)
		}
	}, [settings.autoScroll])

	const scrollToBottom = useCallback(() => {
		if (contentRef.current) {
			contentRef.current.scrollTo({
				top: contentRef.current.scrollHeight,
				behavior: 'smooth',
			})
		}
		// "Jump to end" must also resume live follow so newly transcribed text
		// keeps tracking after the user paused auto-scroll. (MP r11/r130)
		setSettings((prev) => (prev.autoScroll ? prev : { ...prev, autoScroll: true }))
	}, [])

	return (
		<div
			className={`flex flex-col ${className}`}
			style={{ backgroundColor: settings.backgroundColor }}
		>
			{/* Compact header with touch-friendly buttons. Wraps rather than
			    truncating (MU, 7. 10. 2026): with the phone's largest text size the
			    writer's name runs onto more lines and the buttons onto their own row. */}
			<div
				className={`${immersive ? 'hidden' : 'flex'} flex-wrap items-center justify-between gap-x-2 px-3 sm:px-4 py-2 border-b shrink-0`}
				style={{ borderColor: `${settings.textColor}15` }}
			>
				<div className="flex items-center gap-2 min-w-0 grow basis-[calc(8rem*var(--text-scale,1))]">
					<ConnectionBadge
						isConnected={isConnected}
						isConnecting={isConnecting}
						hasWriter={hasWriter}
						accessDenied={accessDenied}
						textColor={settings.textColor}
					/>
					{/* MP-187: show who is transcribing in the unified/session viewer too
					    (the standalone transcript-viewer already does — this surface was missed). */}
					{document?.writerName && (
						<span
							className="min-w-0 text-xs opacity-70 break-words hyphens-auto"
							style={{ color: settings.textColor }}
						>
							{t('viewer.transcript.transcribedBy', { name: document.writerName })}
						</span>
					)}
				</div>
				<div className="flex items-center gap-0.5 ml-auto">
					{error && !hideRefresh && (
						<button
							className="p-2.5 rounded-lg active:bg-black/10 transition-colors text-orange-500"
							onClick={reconnect}
							title={t('viewer.transcript.reconnect')}
						>
							<RefreshCw size={18} />
						</button>
					)}
					{/* Live-follow toggle, same control as the standalone transcript
					    viewer's header (MU 4. 9. 2026): pressed = pinned to the live
					    edge, releases on scroll-up and re-arms at the end; pressing
					    while released jumps to the end and re-pins. Replaces the footer
					    checkbox and the "go to end" button that used to sit here.

					    ON and OFF differ in fill, border and icon (MU 17. 9. 2026) —
					    two similar translucent tints were unreadable on a touch screen,
					    where a latched :hover/:active can outlive the tap. */}
					<button
						type="button"
						className={`p-2.5 rounded-lg border-2 transition-colors ${
							settings.autoScroll
								? 'bg-primary border-primary text-primary-content shadow-soft'
								: 'bg-transparent border-current opacity-60 hover-surface active:bg-black/10'
						}`}
						style={settings.autoScroll ? undefined : { color: settings.textColor }}
						onClick={
							settings.autoScroll
								? () => {
										followTrackerRef.current?.disarm()
										setSettings((prev) => ({ ...prev, autoScroll: false }))
									}
								: scrollToBottom
						}
						title={
							settings.autoScroll
								? t('viewer.transcript.followLiveOff')
								: t('viewer.transcript.followLive')
						}
						aria-label={t('viewer.transcript.followLiveName')}
						aria-pressed={settings.autoScroll}
					>
						{settings.autoScroll ? <ChevronsDown size={18} /> : <ArrowDownToLine size={18} />}
					</button>
					<button
						type="button"
						className={`p-2.5 rounded-lg transition-colors ${showSettings ? 'bg-primary/20' : 'active:bg-black/10'}`}
						style={{ color: settings.textColor }}
						onClick={() => setShowSettings((prev) => !prev)}
						title={t('viewer.transcript.settings.title')}
						aria-label={t('viewer.transcript.settings.title')}
						aria-expanded={showSettings}
					>
						<Settings size={18} />
					</button>
				</div>
			</div>

			{/* Settings panel */}
			{showSettings && (
				<TranscriptSettingsPanel
					settings={settings}
					onChange={setSettings}
					onClose={() => setShowSettings(false)}
					t={t}
					defaults={DEFAULT_SETTINGS}
					presets={LIVE_PRESETS}
					idPrefix="transcript-panel"
					minFontSize={MIN_FONT_SIZE}
					maxFontSize={MAX_FONT_SIZE}
				/>
			)}

			{/* Content */}
			<div
				ref={contentRef}
				// touch-manipulation: iOS would otherwise spend a double tap on its own
				// zoom, and the viewer's double tap to leave full screen would never land.
				// Trade-off: this also turns off iOS's own double-tap-to-zoom here, which
				// some low-vision readers use. Pinch zoom still works (manipulation keeps
				// it), and the font-size slider stays in the viewer's settings.
				className="flex-1 overflow-y-auto px-4 py-4 sm:px-6 touch-manipulation"
				style={{
					backgroundColor: settings.backgroundColor,
					color: settings.textColor,
					fontSize: `${settings.fontSize}px`,
					fontFamily: cssFontFamily(settings.fontFamily),
					fontWeight: settings.fontWeight,
					lineHeight: settings.lineHeight,
					overflowWrap: 'break-word',
					wordBreak: 'break-word',
				}}
				role="region"
				// Focusable so a screen reader or keyboard user can move straight into
				// the text and PageUp/Down scroll it (retest 24. 9. 2026: the text was
				// only reachable by selecting it).
				// biome-ignore lint/a11y/noNoninteractiveTabindex: a scrollable region must be keyboard-reachable (WCAG 2.1.1, axe scrollable-region-focusable).
				tabIndex={0}
				aria-label={t('viewer.transcript.title')}
			>
				<div className="w-full max-w-none">
					{accessDenied ? (
						<div className="flex flex-col items-center justify-center h-full text-center py-12">
							<Lock size={48} className="mb-4 opacity-30" />
							<p className="text-lg font-medium mb-2">
								{t('viewer.transcript.accessDenied.title')}
							</p>
							{isAuthenticated ? (
								<p className="opacity-80 text-sm max-w-xs">
									{t('viewer.transcript.accessDenied.noAccess')}
								</p>
							) : (
								<Link
									to={loginHref}
									className="flex items-center gap-2 px-5 py-2.5 rounded-xl border hover:bg-black/10 active:bg-black/20 transition-colors text-sm font-medium"
									style={{ borderColor: `${settings.textColor}40`, color: settings.textColor }}
								>
									<LogIn size={16} />
									{t('viewer.transcript.accessDenied.signIn')}
								</Link>
							)}
						</div>
					) : !document && !isConnecting && !isConnected ? (
						<div className="flex flex-col items-center justify-center h-full text-center py-12">
							<WifiOff size={48} className="mb-4 opacity-30" />
							<p className="text-lg font-medium mb-2">{t('viewer.transcript.notConnected')}</p>
							<p className="opacity-80 mb-6 text-sm max-w-xs">
								{t('viewer.transcript.notConnectedDesc')}
							</p>
							<button
								className="px-5 py-2.5 rounded-xl border hover:bg-black/10 active:bg-black/20 transition-colors text-sm font-medium"
								style={{ borderColor: `${settings.textColor}40` }}
								onClick={reconnect}
							>
								{t('viewer.transcript.tryAgain')}
							</button>
						</div>
					) : !document && (isConnecting || (isConnected && !hasWriter)) ? (
						<div className="flex flex-col items-center justify-center h-full text-center py-12">
							<Loader2 size={48} className="mb-4 opacity-30 animate-spin" />
							<p className="text-lg font-medium mb-2">
								{isConnecting
									? t('viewer.transcript.connecting')
									: t('viewer.transcript.waitingForWriter')}
							</p>
							<p className="opacity-80 text-sm max-w-xs">
								{isConnecting
									? t('viewer.transcript.connectingDesc')
									: t('viewer.transcript.waitingForWriterDesc')}
							</p>
						</div>
					) : (
						<>
							{document?.paragraphs.map((paragraph) => {
								const Tag = paragraph.headingLevel
									? (`h${paragraph.headingLevel}` as 'h1' | 'h2' | 'h3')
									: 'p'
								return (
									<Tag
										key={paragraph.id}
										className={`mb-4 transition-all duration-200 ${
											activeParagraphId === paragraph.id && isWriterTyping
												? 'border-l-2 border-primary/40 pl-3'
												: ''
										}`}
									>
										{paragraph.segments.map((segment, i) => (
											<span
												key={i}
												style={{
													fontWeight: segment.bold ? 'bold' : undefined,
													fontStyle: segment.italic ? 'italic' : undefined,
													textDecoration: segment.underline ? 'underline' : undefined,
													verticalAlign: segment.subscript
														? 'sub'
														: segment.superscript
															? 'super'
															: undefined,
													fontSize: segment.subscript || segment.superscript ? '0.8em' : undefined,
													...guardSegmentColors(segment.color, segment.backgroundColor),
												}}
											>
												{segment.text}
											</span>
										))}
										{activeParagraphId === paragraph.id && isWriterTyping && (
											<span
												className="inline-block w-0.5 h-[1em] bg-primary/60 animate-pulse ml-0.5 align-text-bottom"
												aria-hidden="true"
											/>
										)}
									</Tag>
								)
							})}
							{isWriterTyping && (
								// F-016: opacity-80 keeps the typing indicator visually
								// subordinate while passing WCAG 1.4.3 (~7:1 effective).
								// Hidden from SR — the off-screen announcer below already
								// signals new text; the visual dots are decorative.
								<p
									className="text-sm opacity-80 flex items-center gap-2"
									style={{ color: settings.textColor }}
									aria-hidden="true"
								>
									<span className="flex gap-0.5" aria-hidden="true">
										<span
											className="w-1.5 h-1.5 rounded-full bg-current animate-bounce"
											style={{ animationDelay: '0ms' }}
										/>
										<span
											className="w-1.5 h-1.5 rounded-full bg-current animate-bounce"
											style={{ animationDelay: '150ms' }}
										/>
										<span
											className="w-1.5 h-1.5 rounded-full bg-current animate-bounce"
											style={{ animationDelay: '300ms' }}
										/>
									</span>
									{t('viewer.transcript.writerTyping')}
								</p>
							)}
						</>
					)}
				</div>
			</div>

			{/*
				F-017: Off-screen polite live region. Receives only newly-appended
				transcript text (debounced) so screen readers announce the delta
				instead of re-reading the whole region on each keystroke. The visible
				region above is a passive role="region" — never live — so the SR only
				ever announces from here.
			*/}
			<div
				ref={announcerRef}
				className="sr-only"
				aria-live="polite"
				aria-atomic="false"
				aria-relevant="additions"
			/>
		</div>
	)
}

function ConnectionBadge({
	isConnected,
	isConnecting,
	hasWriter,
	accessDenied,
	textColor,
}: {
	isConnected: boolean
	isConnecting: boolean
	hasWriter: boolean
	accessDenied: boolean
	textColor: string
}) {
	const { t } = useTranslation()
	// MP-215: a denied subscribe must not read as "Waiting" — the writer will
	// never appear for this viewer.
	if (accessDenied) {
		return (
			<span className="flex items-center gap-1.5 text-red-500">
				<Lock size={14} />
				<span className="text-sm">{t('viewer.status.locked')}</span>
			</span>
		)
	}
	if (isConnecting) {
		return (
			<span className="flex items-center gap-1.5 opacity-80" style={{ color: textColor }}>
				<Loader2 size={14} className="animate-spin" />
				<span className="text-sm">{t('viewer.status.connecting')}</span>
			</span>
		)
	}
	if (isConnected && hasWriter) {
		return (
			<span className="flex items-center gap-1.5 text-green-600">
				<Wifi size={14} />
				<span className="text-sm font-medium">{t('viewer.status.live')}</span>
			</span>
		)
	}
	if (isConnected) {
		return (
			<span className="flex items-center gap-1.5 text-yellow-600">
				<Wifi size={14} />
				<span className="text-sm">{t('viewer.status.waiting')}</span>
			</span>
		)
	}
	return (
		<span className="flex items-center gap-1.5 text-red-500">
			<WifiOff size={14} />
			<span className="text-sm">{t('viewer.status.offline')}</span>
		</span>
	)
}
